import { Link } from "@tanstack/react-router";
import { t } from "i18next";
import { lazy, Suspense, useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { PlanName, ValidationSuscriptionButton } from "@/common/enums/enums";
import { useOwnUsage } from "@/lib/usage-queries";
import type { LimitReason } from "@/pages/Settings/redesign/plan";
import { useAuthStore } from "@/store/auth.store";
import { Button } from "../button";
import { underV3 } from "../v3/flagged";
import { TooltipButton } from "./TooltipButton";

// Loaded on first use: most controls are never blocked.
const LimitReachedSheet = lazy(() =>
  import("./LimitReachedSheet").then((module) => ({
    default: module.LimitReachedSheet,
  })),
);

type ButtonProps = React.ComponentProps<typeof Button>;
interface Props
  extends Pick<
    ButtonProps,
    "onClick" | "size" | "className" | "disabled" | "type" | "variant"
  > {
  children?: React.ReactNode;
  validations?: ValidationSuscriptionButton[];
  // Disables the button for a reason other than plan or usage, shown in its
  // tooltip (e.g. the visit is still processing).
  disabledReason?: string | null;
}
// Plan and usage checks shared by SubscriptionOnlyButton and other controls
// that gate the same actions (e.g. the workspace Create menu).
export const useSubscriptionValidation = (
  validations: ValidationSuscriptionButton[] = [],
) => {
  const { userData, loading: loadingAuth } = useAuthStore((state) => state);
  const needsUsage = validations.includes(
    ValidationSuscriptionButton.RECORDING_USAGES,
  );
  const usage = useOwnUsage(needsUsage);
  const availableMinutes = usage.data
    ? Math.floor(usage.data.subscription.availableSeconds / 60) +
      Math.floor(usage.data.additional.availableSeconds / 60)
    : 0;
  const checks = {
    [ValidationSuscriptionButton.PLAN_FREE_TRIAL]: {
      allowed:
        AppConfig.disablePremiumRestrictions ||
        (!!userData?.planName &&
          userData.planName !== PlanName.FREE_TRIAL.toString()),
      message: t("components.subscriptionOnlyButton.validationPlanFreeTrial"),
    },
    [ValidationSuscriptionButton.RECORDING_USAGES]: {
      allowed: !!usage.data && !usage.error && availableMinutes > 0,
      message: usage.loading
        ? t("common.loading", "Loading...")
        : usage.error
          ? t("error.unknown")
          : t("components.subscriptionOnlyButton.validationRecordingUsages"),
    },
  };
  const failedValidation = validations.find(
    (validation) => !checks[validation].allowed,
  );
  const failed = failedValidation ? checks[failedValidation] : undefined;
  // A real limit (not a loading or failed usage read): no paid plan, or the
  // usage read says no minutes are left.
  const limit: LimitReason | null =
    failedValidation === ValidationSuscriptionButton.PLAN_FREE_TRIAL
      ? "plan"
      : failedValidation === ValidationSuscriptionButton.RECORDING_USAGES &&
          usage.data &&
          !usage.error
        ? "minutes"
        : null;
  return {
    failed,
    limit,
    pending: needsUsage && (loadingAuth || usage.loading),
    usageError: needsUsage && !!usage.error,
    refreshUsage: usage.refresh,
  };
};

export const SubscriptionOnlyButton = ({
  validations = [],
  disabledReason,
  ...props
}: Props): React.ReactNode => {
  const { failed, limit, pending, usageError, refreshUsage } =
    useSubscriptionValidation(validations);
  const [sheetOpen, setSheetOpen] = useState(false);
  // v3.2 app: a control blocked only by the plan or the minutes stays
  // pressable and opens the limit-reached sheet (the server still refuses
  // the action); the classic app keeps the disabled button and tooltip.
  if (
    AppConfig.enableAppRedesign &&
    limit &&
    !props.disabled &&
    !disabledReason &&
    !pending
  )
    return (
      <>
        {/* Same wrappers as TooltipButton, so the layout does not move. */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <Button
              aria-haspopup="dialog"
              className={props.className}
              data-limit={limit}
              size={props.size}
              type="button"
              variant={props.variant}
              onClick={() => setSheetOpen(true)}
            >
              {props.children}
            </Button>
          </div>
        </div>
        {sheetOpen && (
          <Suspense fallback={null}>
            <LimitReachedSheet
              open={sheetOpen}
              reason={limit}
              onOpenChange={setSheetOpen}
            />
          </Suspense>
        )}
      </>
    );
  return (
    <>
      <TooltipButton
        {...props}
        disabled={props.disabled || !!disabledReason || !!failed || pending}
        tooltip={
          disabledReason ? (
            disabledReason
          ) : failed ? (
            <Link to="/settings">{failed.message}</Link>
          ) : null
        }
      >
        {props.children}
      </TooltipButton>
      {usageError && (
        <span
          className={
            underV3(
              "mt-2 flex items-center justify-between gap-3 rounded-xl bg-v3-rec-tint px-4 py-2 font-v3 text-[13.5px] leading-5 text-v3-rec-text",
            ) || undefined
          }
          role="alert"
        >
          {t("error.unknown")}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void refreshUsage();
            }}
          >
            {t("common.retry", "Retry")}
          </Button>
        </span>
      )}
    </>
  );
};
